import { useCallback } from 'react';
import { useIsElectron, useElectronAPI } from '../context/ElectronContext';
import { useProjectDirectories } from '../context/ProjectDirectoriesContext';
import { useNotification } from '../common/components/notification/NotificationContext';
import { useBridgeRequired } from '../context/BridgeRequiredContext';
import { useDirectoryPicker } from './useDirectoryPicker';
import type { ProjectType } from './useProjectSync';
import { DAW_FILE_EXTENSIONS } from '../common/models/enums';

interface UseProjectAutoLocatorOptions {
  project: ProjectType | null;
  projectId: string;
}

const joinPath = (parent: string, child: string) => {
  const separator = parent.includes('\\') ? '\\' : '/';
  return `${parent.replace(/[\\/]+$/, '')}${separator}${child}`;
};

/**
 * Hook for automatically locating project directories
 * Shared project location discovery for page actions
 */
export function useProjectAutoLocator({
  project,
  projectId,
}: UseProjectAutoLocatorOptions) {
  const isElectron = useIsElectron();
  const electronAPI = useElectronAPI();
  const { setProjectDirectory, getAllProjectDirectories } =
    useProjectDirectories();
  const { showError, showSuccess } = useNotification();
  const { requireBridge } = useBridgeRequired();
  const { selectDirectory } = useDirectoryPicker();

  /**
   * Manually locate project folder (user selects directory)
   */
  const handleLocateProject = useCallback(async () => {
    if (!isElectron && !(await requireBridge('locateProject'))) return;

    let dirHandle: FileSystemDirectoryHandle | null = null;
    let filePath: string | undefined;

    // Try Electron API first if available
    if (window.electronAPI?.selectFolder) {
      try {
        const folderPath = await window.electronAPI.selectFolder();
        if (folderPath) {
          filePath = folderPath;
          // Create a mock handle for Electron
          dirHandle = {
            name: folderPath.split(/[/\\]/).pop() || 'Project',
            kind: 'directory' as const,
          } as FileSystemDirectoryHandle;
        }
      } catch (error) {
        console.warn(
          'Electron folder picker failed, falling back to browser:',
          error
        );
      }
    }

    // Fallback to browser picker if Electron failed or not available
    if (!dirHandle) {
      dirHandle = await selectDirectory();
    }

    if (dirHandle) {
      await setProjectDirectory(projectId, dirHandle, filePath);
    }
  }, [
    isElectron,
    projectId,
    requireBridge,
    selectDirectory,
    setProjectDirectory,
  ]);

  /**
   * Auto-locate function that searches for the project folder
   */
  const handleAutoLocateProject = useCallback(async () => {
    if (!project) {
      showError('Project not loaded');
      return;
    }

    if (!isElectron || !electronAPI) {
      // On web, auto-locate relies on the desktop bridge's electronAPI proxy.
      // If neither is available, prompt the user to open the desktop app.
      if (!electronAPI) {
        await requireBridge('autoLocate');
        return;
      }
    }

    try {
      const projectName = project.name;
      const searchPaths: string[] = [];

      // Get recently used parent directories from other projects
      const allDirectories = getAllProjectDirectories();
      const recentPaths = Object.values(allDirectories)
        .map((dir) => dir.filePath)
        .filter((path): path is string => !!path)
        .map((path) => {
          // Get parent directory
          const parts = path.split(/[/\\]/);
          parts.pop(); // Remove the project folder name
          return parts.join(path.includes('\\') ? '\\' : '/');
        })
        .filter((path, index, self) => self.indexOf(path) === index) // Unique
        .slice(0, 5); // Limit to 5 most recent

      // Add recently used parent directories first (most likely location)
      searchPaths.push(...recentPaths);

      // Normalize project name for matching (remove special chars, lowercase)
      const normalizeName = (name: string) =>
        name.toLowerCase().replace(/[^a-z0-9]/g, '');

      const normalizedProjectName = normalizeName(projectName);
      const foundPaths: Array<{ path: string; score: number }> = [];

      // Search in each location
      for (const searchPath of searchPaths) {
        try {
          const result = await electronAPI.readDirectory(searchPath);
          if (result.success && result.items) {
            for (const item of result.items) {
              if (item.isDirectory) {
                const normalizedItemName = normalizeName(item.name);

                // Exact match gets highest score
                if (normalizedItemName === normalizedProjectName) {
                  foundPaths.push({
                    path: joinPath(searchPath, item.name),
                    score: 100,
                  });
                }
                // Contains project name gets medium score
                else if (
                  normalizedItemName.includes(normalizedProjectName) ||
                  normalizedProjectName.includes(normalizedItemName)
                ) {
                  foundPaths.push({
                    path: joinPath(searchPath, item.name),
                    score: 50,
                  });
                }
              }
            }
          }
        } catch (error) {
          // Skip directories we can't access
          continue;
        }
      }

      // Sort by score and check for DAW files
      foundPaths.sort((a, b) => b.score - a.score);

      for (const candidate of foundPaths) {
        try {
          // Check if folder contains DAW project files
          const dirResult = await electronAPI.readDirectory(candidate.path);
          if (dirResult.success && dirResult.items) {
            const dawExtensions = DAW_FILE_EXTENSIONS;
            const hasDAWFile = dirResult.items.some(
              (item) =>
                item.isFile &&
                dawExtensions.some((ext) =>
                  item.name.toLowerCase().endsWith(ext)
                )
            );

            if (hasDAWFile) {
              // Found a match! Save it
              const filePath = candidate.path;
              const dirHandle = {
                name: filePath.split(/[/\\]/).pop() || 'Project',
                kind: 'directory' as const,
              } as FileSystemDirectoryHandle;

              await setProjectDirectory(projectId, dirHandle, filePath);
              showSuccess(`Auto-located project at: ${filePath}`);
              return filePath;
            }
          }
        } catch (error) {
          // Continue searching
          continue;
        }
      }

      // No match found
      showError(
        `Could not automatically locate "${projectName}". Please use "Locate Project" to select it manually.`
      );
      return null;
    } catch (error) {
      console.error('Auto locate error:', error);
      showError(
        'Failed to auto-locate project. Please try "Locate Project" instead.'
      );
      return null;
    }
  }, [
    project,
    projectId,
    isElectron,
    electronAPI,
    getAllProjectDirectories,
    setProjectDirectory,
    showError,
    showSuccess,
    requireBridge,
  ]);

  return {
    handleLocateProject,
    handleAutoLocateProject,
  };
}
